import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Pagination, Tab, Nav, Form, Alert } from 'react-bootstrap';
import { format } from 'date-fns';
import api from '../utils/api';

// Types
interface Offer {
  offer_id: number;
  code: string;
  description: string;
  service_type: 'flight' | 'hotel' | 'both';
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  valid_from: string;
  valid_until: string;
  status: 'active' | 'inactive';
}

interface Coupon {
  coupon_id: number;
  code: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_booking_amount?: number;
  max_uses?: number;
  uses_count: number;
  valid_from: string;
  valid_until: string;
  status: 'active' | 'inactive';
}

type ActiveTab = 'flight' | 'hotel' | 'coupons';

const DiscountsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('flight');

  // State for flight offers
  const [flightOffers, setFlightOffers] = useState<Offer[]>([]);
  const [flightTotal, setFlightTotal] = useState(0);
  const [flightPage, setFlightPage] = useState(1);
  const [flightPerPage] = useState(10);

  // State for hotel offers
  const [hotelOffers, setHotelOffers] = useState<Offer[]>([]);
  const [hotelTotal, setHotelTotal] = useState(0);
  const [hotelPage, setHotelPage] = useState(1);
  const [hotelPerPage] = useState(10);

  // State for coupons
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponTotal, setCouponTotal] = useState(0);
  const [couponPage, setCouponPage] = useState(1);
  const [couponPerPage] = useState(10);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Offer | Coupon | null>(null);
  const [modalType, setModalType] = useState<'offer' | 'coupon'>('offer');
  const [formData, setFormData] = useState<any>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'flight') fetchOffers('flight', flightPage);
    else if (activeTab === 'hotel') fetchOffers('hotel', hotelPage);
    else fetchCoupons(couponPage);
  }, [activeTab, flightPage, hotelPage, couponPage]);

  const fetchOffers = async (type: 'flight' | 'hotel', page: number) => {
    try {
      setLoading(true);
      const data = await api.getOffers(page, 10);
      const filtered = data.offers.filter((offer: Offer) =>
        type === 'flight'
          ? offer.service_type === 'flight' || offer.service_type === 'both'
          : offer.service_type === 'hotel' || offer.service_type === 'both'
      );

      if (type === 'flight') {
        setFlightOffers(filtered);
        setFlightTotal(data.total);
      } else {
        setHotelOffers(filtered);
        setHotelTotal(data.total);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async (page: number) => {
    try {
      setLoading(true);
      const data = await api.getCoupons(page, 10);
      setCoupons(data.coupons);
      setCouponTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number, type: 'offer' | 'coupon') => {
    if (!window.confirm('Are you sure you want to deactivate this item?')) return;
    try {
      setLoading(true);
      if (type === 'offer') {
        await api.deleteOffer(id);
      } else {
        await api.deleteCoupon(id);
      }

      if (activeTab === 'flight') fetchOffers('flight', flightPage);
      else if (activeTab === 'hotel') fetchOffers('hotel', hotelPage);
      else fetchCoupons(couponPage);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item?: Offer | Coupon) => {
    if (item) {
      if ('offer_id' in item) {
        setModalType('offer');
        setFormData({
          ...item,
          valid_from: item.valid_from.slice(0, 16),
          valid_until: item.valid_until.slice(0, 16),
        });
      } else {
        setModalType('coupon');
        setFormData({
          ...item,
          valid_from: item.valid_from.slice(0, 16),
          valid_until: item.valid_until.slice(0, 16),
        });
      }
      setEditingItem(item);
    } else {
      setEditingItem(null);
      if (activeTab === 'coupons') {
        setModalType('coupon');
        setFormData({
          code: '',
          description: '',
          discount_type: 'percentage',
          discount_value: '',
          min_booking_amount: '',
          max_uses: '',
          valid_from: '',
          valid_until: '',
          status: 'active',
        });
      } else {
        setModalType('offer');
        setFormData({
          code: '',
          description: '',
          service_type: activeTab === 'flight' ? 'flight' : 'hotel',
          discount_type: 'percentage',
          discount_value: '',
          valid_from: '',
          valid_until: '',
          status: 'active',
        });
      }
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setLoading(true);

      if (modalType === 'offer') {
        if (editingItem) {
          await api.updateOffer((editingItem as Offer).offer_id, formData);
        } else {
          await api.createOffer(formData);
        }
      } else {
        if (editingItem) {
          await api.updateCoupon((editingItem as Coupon).coupon_id, formData);
        } else {
          await api.createCoupon(formData);
        }
      }

      setShowModal(false);
      if (activeTab === 'flight') fetchOffers('flight', flightPage);
      else if (activeTab === 'hotel') fetchOffers('hotel', hotelPage);
      else fetchCoupons(couponPage);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = (currentPage: number, total: number, onPageChange: (page: number) => void) => {
    const totalPages = Math.ceil(total / 10);
    if (totalPages <= 1) return null;
    return (
      <Pagination>
        <Pagination.Prev
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        />
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
          <Pagination.Item key={p} active={p === currentPage} onClick={() => onPageChange(p)}>
            {p}
          </Pagination.Item>
        ))}
        <Pagination.Next
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  return (
    <div className="discounts-page">
      <h2 className="mb-4">Discounts & Coupons</h2>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k as ActiveTab)}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="flight">Flight Offers</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="hotel">Hotel Offers</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="coupons">Coupons</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Flight Offers Tab */}
          <Tab.Pane eventKey="flight">
            <div className="d-flex justify-content-between mb-3">
              <h4>Flight Offers</h4>
              <Button variant="primary" onClick={() => openModal()}>Add Flight Offer</Button>
            </div>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Discount</th>
                  <th>Valid From</th>
                  <th>Valid Until</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {flightOffers.map(offer => (
                  <tr key={offer.offer_id}>
                    <td>{offer.offer_id}</td>
                    <td>{offer.code}</td>
                    <td>{offer.description}</td>
                    <td>{offer.discount_value}{offer.discount_type === 'percentage' ? '%' : ' fixed'}</td>
                    <td>{format(new Date(offer.valid_from), 'dd/MM/yyyy')}</td>
                    <td>{format(new Date(offer.valid_until), 'dd/MM/yyyy')}</td>
                    <td>{offer.status}</td>
                    <td>
                      <Button size="sm" variant="outline-secondary" onClick={() => openModal(offer)}>Edit</Button>{' '}
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(offer.offer_id, 'offer')}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {renderPagination(flightPage, flightTotal, setFlightPage)}
          </Tab.Pane>

          {/* Hotel Offers Tab */}
          <Tab.Pane eventKey="hotel">
            <div className="d-flex justify-content-between mb-3">
              <h4>Hotel Offers</h4>
              <Button variant="primary" onClick={() => openModal()}>Add Hotel Offer</Button>
            </div>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Discount</th>
                  <th>Valid From</th>
                  <th>Valid Until</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {hotelOffers.map(offer => (
                  <tr key={offer.offer_id}>
                    <td>{offer.offer_id}</td>
                    <td>{offer.code}</td>
                    <td>{offer.description}</td>
                    <td>{offer.discount_value}{offer.discount_type === 'percentage' ? '%' : ' fixed'}</td>
                    <td>{format(new Date(offer.valid_from), 'dd/MM/yyyy')}</td>
                    <td>{format(new Date(offer.valid_until), 'dd/MM/yyyy')}</td>
                    <td>{offer.status}</td>
                    <td>
                      <Button size="sm" variant="outline-secondary" onClick={() => openModal(offer)}>Edit</Button>{' '}
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(offer.offer_id, 'offer')}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {renderPagination(hotelPage, hotelTotal, setHotelPage)}
          </Tab.Pane>

          {/* Coupons Tab */}
          <Tab.Pane eventKey="coupons">
            <div className="d-flex justify-content-between mb-3">
              <h4>Coupons</h4>
              <Button variant="primary" onClick={() => openModal()}>Add Coupon</Button>
            </div>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Discount</th>
                  <th>Min Amount</th>
                  <th>Uses</th>
                  <th>Valid From</th>
                  <th>Valid Until</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map(coupon => (
                  <tr key={coupon.coupon_id}>
                    <td>{coupon.coupon_id}</td>
                    <td>{coupon.code}</td>
                    <td>{coupon.description}</td>
                    <td>{coupon.discount_value}{coupon.discount_type === 'percentage' ? '%' : ' fixed'}</td>
                    <td>{coupon.min_booking_amount || '-'}</td>
                    <td>{coupon.uses_count}/{coupon.max_uses || '∞'}</td>
                    <td>{format(new Date(coupon.valid_from), 'dd/MM/yyyy')}</td>
                    <td>{format(new Date(coupon.valid_until), 'dd/MM/yyyy')}</td>
                    <td>{coupon.status}</td>
                    <td>
                      <Button size="sm" variant="outline-secondary" onClick={() => openModal(coupon)}>Edit</Button>{' '}
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(coupon.coupon_id, 'coupon')}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {renderPagination(couponPage, couponTotal, setCouponPage)}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItem ? 'Edit' : 'Add'} {modalType === 'offer' ? 'Offer' : 'Coupon'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </Form.Group>

            {modalType === 'offer' && (
              <Form.Group className="mb-3">
                <Form.Label>Service Type</Form.Label>
                <Form.Select
                  value={formData.service_type || 'flight'}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  required
                >
                  <option value="flight">Flight</option>
                  <option value="hotel">Hotel</option>
                  <option value="both">Both</option>
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Discount Type</Form.Label>
              <Form.Select
                value={formData.discount_type || 'percentage'}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                required
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Discount Value</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={formData.discount_value || ''}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                required
              />
            </Form.Group>

            {modalType === 'coupon' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Min Booking Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={formData.min_booking_amount || ''}
                    onChange={(e) => setFormData({ ...formData, min_booking_amount: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Max Uses</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.max_uses || ''}
                    onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  />
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Valid From</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.valid_from || ''}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Valid Until</Form.Label>
              <Form.Control
                type="datetime-local"
                value={formData.valid_until || ''}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={formData.status || 'active'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DiscountsPage;